import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Darkmode from "./Darkmode";
import { useDispatch, useSelector } from "react-redux";

// Modal Component
const MenuModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-900 p-6 rounded-lg shadow-lg w-64">
        {children}
        <button
          onClick={onClose}
          className="mt-4 w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};

const Navbar = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.userinfo);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const logoutHandle = () => {
    localStorage.removeItem("AccessToken");
    localStorage.removeItem("refreshToken");
    dispatch({ type: "auth/logout" });
    document.title = "Attendance portal";
  };

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const navPathActive = (path) => location.pathname === path;
  /** Pill nav — dark: always light text on hover/active (readable on charcoal bar) */
  const pillLinkClass = (path) => {
    const on = navPathActive(path);
    const base =
      "group flex items-center gap-2 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] font-semibold tracking-tight transition-colors duration-200 outline-none";
    if (on) {
      return `${base} font-bold text-gray-900 dark:text-white`;
    }
    return `${base} text-gray-600 hover:text-gray-900 hover:bg-black/[0.05] dark:text-neutral-300 dark:hover:bg-zinc-700 dark:hover:text-white`;
  };
  const pillIconClass = (path) => {
    const on = navPathActive(path);
    return `w-[17px] h-[17px] shrink-0 transition-colors ${
      on
        ? "text-gray-800 dark:text-white"
        : "text-gray-500 group-hover:text-gray-900 dark:text-neutral-400 dark:group-hover:text-white"
    }`;
  };

  return (
    <div className="">
      {/* Top Bar */}
      <div className="dark:bg-[#0f172a] bg-gray-100 border-b border-gray-200 dark:border-gray-800/50 transition-colors duration-300">
        <div className="container mx-auto ">
          <div className="flex flex-row items-center justify-between h-10">
            {/* User Info Section (Responsive) */}
            <div className="flex items-center group overflow-hidden flex-1">
              <div className="flex items-center bg-white/50 dark:bg-white/5 px-3 py-1.5 rounded-[12px] sm:rounded-full border border-gray-200/50 dark:border-gray-700/30">
                <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                  <span className=" tracking-wide text-[10px] sm:text-[11px] font-extrabold text-gray-900 dark:text-gray-100  truncate">
                    {userProfile.MemberName}
                  </span>
                  <span className="hidden sm:block mx-2 w-[1px] h-3 bg-gray-300 dark:bg-gray-700"></span>
                  <span className="text-[9px] sm:text-[10px] font-medium text-gray-500 dark:text-gray-400 sm:mt-0 opacity-90 truncate">
                    {userProfile.Designation}
                  </span>
                </div>
              </div>
            </div>


            {/* Utility Section */}
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <div className="flex items-center bg-white/40 dark:bg-gray-800/40 rounded-lg p-0.5 border border-gray-200/50 dark:border-gray-700/50">
                <Darkmode />
              </div>
              
              <div className="h-4 w-[1px] bg-gray-300 dark:bg-gray-700 mx-0.5 sm:mx-1"></div>

              <Link 
                onClick={logoutHandle}
                className="group relative flex items-center justify-center w-8 h-8 rounded-lg hover:bg-rose-500/10 active:scale-95 transition-all duration-300"
                title="Logout"
              >
                <svg
                  className="w-4 h-4 text-gray-500 group-hover:text-rose-500 transition-colors"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="container mx-auto">
        <div className="text-left my-3 flex flex-row items-center">
          {/* Logo Section */}
          <Link to="/">
            <p className="left-3 py-1 w-64 text-2xl font-semibold text-blue-900 dark:text-blue-600">
              Gentrick Staffing
            </p>
            <p className="pl-1 w-64 -mt-3 text-sm text-blue-900 dark:text-blue-600">
              Attendance Portal
            </p>
          </Link>

          {/* Menu Button for Mobile */}
          <button
            className="ml-auto lg:hidden focus:outline-none"
            onClick={toggleModal}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              ></path>
            </svg>
          </button>

          {/* Navbar Right Side (Desktop) — full pill bar like reference */}
          <div className="ml-auto hidden lg:block">
            <nav className="flex justify-end" aria-label="Main navigation">
              <ul
                className="inline-flex flex-row flex-wrap items-center gap-0.5 rounded-full border border-gray-200/90 bg-gray-100/95 px-1 py-1 shadow-sm
                  dark:border-white/[0.1] dark:bg-[#1a1a1a] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.06),0_4px_24px_-8px_rgba(0,0,0,0.75)]"
              >
                {userProfile.MemberRole == 2 && (
                  <li>
                    <Link to="/members" className={pillLinkClass("/members")}>
                      <svg
                        className={pillIconClass("/members")}
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.9 3C3.9 3 3 3.8 3 4.9V9c0 1 .8 1.9 1.9 1.9H9c1 0 1.9-.8 1.9-1.9V5c0-1-.8-1.9-1.9-1.9H5Zm10 0c-1 0-1.9.8-1.9 1.9V9c0 1 .8 1.9 1.9 1.9H19c1 0 1.9-.8 1.9-1.9V5c0-1-.8-1.9-1.9-1.9h-4Zm-10 10c-1 0-1.9.8-1.9 1.9V19c0 1 .8 1.9 1.9 1.9H9c1 0 1.9-.8 1.9-1.9v-4c0-1-.8-1.9-1.9-1.9H5Zm10 0c-1 0-1.9.8-1.9 1.9V19c0 1 .8 1.9 1.9 1.9H19c1 0 1.9-.8 1.9-1.9v-4c0-1-.8-1.9-1.9-1.9h-4Z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Members</span>
                    </Link>
                  </li>
                )}

                <li>
                  <Link to="/invoice" className={pillLinkClass("/invoice")}>
                    <svg
                      className={pillIconClass("/invoice")}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M10 3v4c0 .6-.4 1-1 1H5m8-2h3m-3 3h3m-4 3v6m4-3H8M19 4v16c0 .6-.4 1-1 1H6a1 1 0 0 1-1-1V8c0-.4.1-.6.3-.8l4-4 .6-.2H18c.6 0 1 .4 1 1ZM8 12v6h8v-6H8Z"
                      />
                    </svg>
                    <span>Invoice</span>
                  </Link>
                </li>

                <li>
                  <Link to="/timesheet" className={pillLinkClass("/timesheet")}>
                    <svg
                      className={pillIconClass("/timesheet")}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14c.6 0 1-.4 1-1V7c0-.6-.4-1-1-1H5a1 1 0 0 0-1 1v12c0 .6.4 1 1 1Zm3-7h0v0h0v0Zm4 0h0v0h0v0Zm4 0h0v0h0v0Zm-8 4h0v0h0v0Zm4 0h0v0h0v0Zm4 0h0v0h0v0Z"
                      />
                    </svg>
                    <span>TimeSheet</span>
                  </Link>
                </li>

                <li>
                  <Link to="/medical" className={pillLinkClass("/medical")}>
                    <svg
                      className={pillIconClass("/medical")}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Zm-1-13h2v5h-2Zm0 6h2v2h-2Z"
                      />
                    </svg>
                    <span>Medical</span>
                  </Link>
                </li>

                <li>
                  <Link to="/requests" className={pillLinkClass("/requests")}>
                    <svg
                      className={pillIconClass("/requests")}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="square"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h1m4-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm7.4 1.6a2 2 0 0 1 0 2.7l-6 6-3.4.7.7-3.4 6-6a2 2 0 0 1 2.7 0Z"
                      />
                    </svg>
                    <span>Request</span>
                  </Link>
                </li>

                <li>
                  <Link to="/summary" className={pillLinkClass("/summary")}>
                    <svg
                      className={pillIconClass("/summary")}
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="square"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h1m4-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm7.4 1.6a2 2 0 0 1 0 2.7l-6 6-3.4.7.7-3.4 6-6a2 2 0 0 1 2.7 0Z"
                      />
                    </svg>
                    <span>Summary</span>
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Modal for Mobile Menu */}
      <MenuModal isOpen={isModalOpen} onClose={toggleModal}>
        <ul className="space-y-4">
          {userProfile.MemberRole == 2 && (
            <li>
              <Link
                to="/members"
                className={`px-1.5 border-none outline-none cursor-pointer transition duration-700 flex items-center p-1 rounded-lg ${location.pathname === "/members"
                  ? "font-semibold dark:text-gray-300 text-gray-700"
                  : "text-gray-500 dark:text-gray-500"
                  }`}
              >
                <svg
                  className="w-5 h-4"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.9 3C3.9 3 3 3.8 3 4.9V9c0 1 .8 1.9 1.9 1.9H9c1 0 1.9-.8 1.9-1.9V5c0-1-.8-1.9-1.9-1.9H5Zm10 0c-1 0-1.9.8-1.9 1.9V9c0 1 .8 1.9 1.9 1.9H19c1 0 1.9-.8 1.9-1.9V5c0-1-.8-1.9-1.9-1.9h-4Zm-10 10c-1 0-1.9.8-1.9 1.9V19c0 1 .8 1.9 1.9 1.9H9c1 0 1.9-.8 1.9-1.9v-4c0-1-.8-1.9-1.9-1.9H5Zm10 0c-1 0-1.9.8-1.9 1.9V19c0 1 .8 1.9 1.9 1.9H19c1 0 1.9-.8 1.9-1.9v-4c0-1-.8-1.9-1.9-1.9h-4Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="flex-1 ms-0.5 text-sm">Members</span>
              </Link>
            </li>
          )}

          {userProfile.MemberRole == 2 && (
            <li>
              <Link
                to="/invoice"
                className={`px-1.5 border-none outline-none cursor-pointer transition duration-700 flex items-center p-1 rounded-lg ${location.pathname === "/invoice"
                  ? "font-semibold dark:text-gray-300 text-gray-700"
                  : "text-gray-500 dark:text-gray-500"
                  }`}
              >
                <svg
                  className="w-5 h-4"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 3v4c0 .6-.4 1-1 1H5m8-2h3m-3 3h3m-4 3v6m4-3H8M19 4v16c0 .6-.4 1-1 1H6a1 1 0 0 1-1-1V8c0-.4.1-.6.3-.8l4-4 .6-.2H18c.6 0 1 .4 1 1ZM8 12v6h8v-6H8Z"
                  />
                </svg>
                <span className="flex-1 ms-0.5 text-sm">Invoice</span>
              </Link>
            </li>
          )}



          <li>
            <Link
              to="/timesheet"
              className={`px-1.5 border-none outline-none cursor-pointer transition duration-700 flex items-center p-1 rounded-lg ${location.pathname === "/timesheet"
                ? "font-semibold dark:text-gray-300 text-gray-700"
                : "text-gray-500 dark:text-gray-500"
                }`}
            >
              <svg
                className="w-5 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14c.6 0 1-.4 1-1V7c0-.6-.4-1-1-1H5a1 1 0 0 0-1 1v12c0 .6.4 1 1 1Zm3-7h0v0h0v0Zm4 0h0v0h0v0Zm4 0h0v0h0v0Zm-8 4h0v0h0v0Zm4 0h0v0h0v0Zm4 0h0v0h0v0Z"
                />
              </svg>
              <span className="flex-1 ms-0.5 text-sm">TimeSheet</span>
            </Link>
          </li>
          <li>
            <Link
              to="/medical"
              className={`px-1.5 border-none outline-none cursor-pointer transition duration-700 flex items-center p-1 rounded-lg ${location.pathname === "/medical"
                ? "font-semibold dark:text-gray-300 text-gray-700"
                : "text-gray-500 dark:text-gray-500"
                }`}
            >
              <svg
                className="w-5 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Zm-1-13h2v5h-2Zm0 6h2v2h-2Z"
                />
              </svg>
              <span className="flex-1 ms-0.5 text-sm">Medical</span>
            </Link>
          </li>
          <li>
            <Link
              to="/requests"
              className={`px-1.5 border-none outline-none cursor-pointer transition duration-700 flex items-center p-1 rounded-lg ${location.pathname === "/requests"
                ? "font-semibold dark:text-gray-300 text-gray-700"
                : "text-gray-500 dark:text-gray-500"
                }`}
            >
              <svg
                className="w-5 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="square"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h1m4-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm7.4 1.6a2 2 0 0 1 0 2.7l-6 6-3.4.7.7-3.4 6-6a2 2 0 0 1 2.7 0Z"
                />
              </svg>
              <span className="flex-1 ms-0.5 text-sm">Request</span>
            </Link>
          </li>

          <li>
            <Link
              to="/summary"
              className={`px-1.5 border-none outline-none cursor-pointer transition duration-700 flex items-center p-1 rounded-lg ${location.pathname === "/summary"
                ? "font-semibold dark:text-gray-300 text-gray-700"
                : "text-gray-500 dark:text-gray-500"
                }`}
            >
              <svg
                className="w-5 h-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="square"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h1m4-6a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm7.4 1.6a2 2 0 0 1 0 2.7l-6 6-3.4.7.7-3.4 6-6a2 2 0 0 1 2.7 0Z"
                />
              </svg>
              <span className="flex-1 ms-0.5 text-sm">Summary</span>
            </Link>
          </li>        </ul>
      </MenuModal>
    </div>
  );
};

export default Navbar;